import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import {
    BookOpen,
} from "lucide-react";
import Naray8BackButton from "../../Components/Naray8/ui/Naray8BackButton";
import { useGetNaray8byId } from "../../hooks/api/useNaray8";
import { HiOutlineBookOpen } from "react-icons/hi";
import { CiBookmark } from "react-icons/ci";
import { FaBookmark, FaCalendarAlt, FaLock, FaStar } from "react-icons/fa";
import { PiShareFatFill } from "react-icons/pi";
import { IoEye } from "react-icons/io5";
import { capitalizeFirstLetter } from "../../utils/stringUtils";
import { useSelector } from "react-redux";
import { RiDraftFill } from "react-icons/ri";
import moment from "moment";
import { formatNumber } from "../../helper";
import Narray8Comment from "../../Components/Naray8/Comments/Narray8Comment";
import { toast } from "react-toastify";
import { addToNaray8Library, removeFromNaray8Library, checkFollowStatus } from "../../redux/naray8/naray8Action";
import { useQueryClient } from "@tanstack/react-query";
import FollowButton from "../../Components/Naray8/ui/FollowButton";
import ChapterPurchaseFlow from "../../Components/Naray8/Modals/ChapterModalPurchases";
import RatingModal from "../../Components/Naray8/Modals/RatingModal";
import defaultAvatar from "../../assests/defaultImage.png"
import BDcoin from "../../Components/BDcoin";


const Badge = ({ icon, text }) => (
    <div className="flex items-center gap-1 bg-white/15 px-3 py-1 rounded-[4px]">{icon}<span className="font-sora text-sm -mb-0.5">{text}</span></div>
);


const LAST_READ_KEY = "naray8:lastReadChapter";

const CHAPTERS_PER_ROW = 6;
const DEFAULT_ROWS = 5;
const DEFAULT_VISIBLE = CHAPTERS_PER_ROW * DEFAULT_ROWS;

export default function StoryDetailPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { narrateId } = useParams();
    const { data, refetch } = useGetNaray8byId(narrateId);
    const myId = useSelector((state) => state.user.userData.userId);
    const { title, coverImage, chapters, description, tags, totalChapters, viewCount, createdAt, ratings, contentStatus, user, isInLibrary, lastReadChapterId } = data ?? {};
    const { firstName, lastName, userId, profilePhoto, UserName, isFollowing: initialIsFollowing } = user ?? {}
    const profileImage = profilePhoto && profilePhoto?.length > 0 ? profilePhoto[0].photoPublicUrl : null;
    const isAuthor = myId === userId;
    const authorName = `${firstName} ${lastName}`;
    const [showAllChapters, setShowAllChapters] = useState(false);
    const [addingToLibrary, setAddingToLibrary] = useState(false);
    const [addedToLibrary, setAddedToLibrary] = useState(isInLibrary);
    const [currentFollowStatus, setCurrentFollowStatus] = useState(initialIsFollowing);

    const [unlockModalOpen, setUnlockModalOpen] = useState(null);
    const [searchParams] = useSearchParams();
    const isStatisticQuery = searchParams.get("stats") === "true";
    const showStats = isAuthor && isStatisticQuery;
    const [showRatingModal, setShowRatingModal] = useState(false);

    const lastRead = JSON.parse(localStorage.getItem(LAST_READ_KEY)) ?? null;

    // Fetch fresh follow status when component mounts or userId changes
    useEffect(() => {
        const fetchFollowStatus = async () => {
            if (userId && myId && !isAuthor) {
                try {
                    const followStatus = await checkFollowStatus(userId);
                    setCurrentFollowStatus(followStatus);
                } catch (error) {
                    console.error("Error fetching follow status:", error);
                }
            }
        };
        
        fetchFollowStatus();
    }, [userId, myId, isAuthor]);

    // Update follow status when data refetches
    useEffect(() => {
        setCurrentFollowStatus(initialIsFollowing);
    }, [initialIsFollowing]);

    // ✅ FIX #1: Force refresh follow status when story page loads (for Follow Button bug)
    useEffect(() => {
        const refreshFollowStatus = async () => {
            if (userId && myId && !isAuthor) {
                try {
                    const freshFollowStatus = await checkFollowStatus(userId);
                    setCurrentFollowStatus(freshFollowStatus);
                } catch (error) {
                    console.error("Error refreshing follow status:", error);
                }
            }
        };
        
        refreshFollowStatus();
    }, [userId, myId, isAuthor, narrateId]);

    /* ================= CHAPTER CLICK ================= */
    const handleChapterClick = (chapter) => {
        const isUnlocked = chapter.canRead || isAuthor

        if (isUnlocked) {
            navigate(`/naray8/b/${narrateId}/chapter/${chapter.chapterId}`);
            return;
        }

        setUnlockModalOpen(chapter);
    };

    const visibleChapters = showAllChapters
        ? chapters
        : chapters?.slice(0, DEFAULT_VISIBLE);

    const handleAddToLibrary = async () => {
        try {
            setAddingToLibrary(true);
            if (addedToLibrary) {
                const removedFromLibrary = await removeFromNaray8Library(narrateId)
                if (removedFromLibrary) {
                    toast.success("Removed from your library!");
                    queryClient.invalidateQueries({ queryKey: ["naray8", "library"] })
                    setAddedToLibrary(false);
                }
            } else {
                const addedToLibraryResponse = await addToNaray8Library(narrateId)
                if (addedToLibraryResponse) {
                    toast.success("Added to your library!");
                    queryClient.invalidateQueries({ queryKey: ["naray8", "library"] })
                    setAddedToLibrary(true);
                }
            }
        }
        catch (error) {
            if (addedToLibrary) {
                toast.error("Failed to remove from library. Please try again.");
            } else {
                toast.error("Failed to add to library. Please try again.");
            }
        } finally {
            setAddingToLibrary(false);
        }
    }

    // ✅ FIX #2: Updated handleFollowChange with proper cache invalidation
    const handleFollowChange = async (newFollowStatus) => {
        setCurrentFollowStatus(newFollowStatus);
        // Invalidate queries to refresh cached data
        queryClient.invalidateQueries({ queryKey: ["naray8", narrateId] });
        queryClient.invalidateQueries({ queryKey: ["naray8-writer"] });
        // Refresh the story data
        await refetch();
    };

    // ✅ FIX #3: Handle rating modal close with refresh
    const handleRatingModalClose = () => {
        setShowRatingModal(false);
        // Force refresh story data to get updated rating
        refetch();
        // Also invalidate the query cache
        queryClient.invalidateQueries({ queryKey: ["naray8", narrateId] });
    };

    // Create author object with current follow status for FollowButton
    const authorWithFollowStatus = {
        ...user,
        isFollowing: currentFollowStatus
    };

    return (
        <div className="font-sora bg-customGray rounded-[10px] text-white overflow-hidden">
            <section className={`${showStats ? "" : "p-4"}`}>
                <div className={`relative w-full ${showStats ? "aspect-[4.8251]" : "aspect-[3.23/1]"} flex justify-center overflow-hidden`}>
                    <Naray8BackButton className={"absolute left-2 top-2 z-30"} />

                    <img
                        src={coverImage}
                        alt="cover"
                        className="absolute inset-0 w-full h-full object-top object-cover"
                    />

                    <div className="absolute inset-0 bg-white/15 z-10" />

                    <img
                        src={coverImage}
                        alt="book"
                        className={`min-w-[200px] ${showStats ? "aspect-[200/223]" : "aspect-[9/16]"} rounded-[4px] object-cover z-20`}
                    />
                </div>
            </section>

            {!showStats &&
                <section className="px-4 flex flex-col gap-1">
                    <button
                        onClick={() => {
                            if (lastReadChapterId) {
                                const index = chapters?.findIndex(ch => ch.chapterId === lastReadChapterId);
                                if (index !== -1) {
                                    handleChapterClick(chapters[index]);
                                    return;
                                } else {
                                    toast.info("Chapter not found!")
                                }
                            } else {
                                handleChapterClick(chapters?.[0]);
                            }
                        }}
                        className="h-[50px] w-full sm:w-auto bg-customDark rounded-[5px] flex items-center justify-center gap-2 font-outfit text-[15px]"
                    >
                        <HiOutlineBookOpen color="#9B9B9B" size={18} />
                        {lastReadChapterId ? "Continue Reading" : "Start Reading"}
                    </button>

                    <button
                        disabled={addingToLibrary}
                        onClick={handleAddToLibrary}
                        className="h-[50px] w-full sm:w-auto bg-white/15 rounded-[5px] flex items-center justify-center gap-2 font-outfit text-[15px] disabled:opacity-70"
                    >
                        {addedToLibrary ? <FaBookmark size={18} /> : <CiBookmark size={18} />} {addedToLibrary ? "Remove from Library" : 'Add to Library'}
                    </button>
                </section>
            }

            {!showStats &&
                <>
                    {/* ================= STORY INFO ================= */}
                    <section className="px-4 mt-4">
                        <h1 className="text-xl sm:text-2xl md:text-4xl font-sora">{title}</h1>
                        <p className="text-white font-sora mt-1 text-sm">by {authorName}</p>

                        <div className="flex flex-wrap gap-2 mt-4 text-sm">
                            <Badge icon={<FaStar size={14} className="text-[#FFBC00]" />} text={`${Number(ratings).toFixed(1)} Rating`} />
                            <Badge icon={null} text={`${totalChapters} chapters`} />
                            <Badge icon={<IoEye size={14} />} text={`${formatNumber(viewCount)} Views`} />
                            <Badge icon={<FaCalendarAlt size={14} />} text={moment(createdAt).format("MMMM YYYY")} />
                        </div>
                    </section>

                    <section className="px-4 mt-4">
                        <h1 className="text-[12px] ">Genres</h1>
                        <div className="flex flex-wrap gap-2 text-sm">
                            {tags?.map((genre, index) => (
                                <div key={`${index}`} className="flex items-center text-[9.85px] text-customDark font-sora font-semibold gap-1 bg-[#9B9B9B] px-3 py-0.5 rounded-[4px]">{capitalizeFirstLetter(genre)}</div>
                            ))}
                        </div>
                    </section>

                    {/* ================= DESCRIPTION ================= */}
                    <section className="px-4 mt-4 text-sm text-white font-sora font-light border-b border-white pb-4">
                        <h1 className="text-[14px] font-semibold">Description</h1>
                        <p className="text-white text-[11px] leading-[14px] font-sora mt-1">
                            {description}
                        </p>
                    </section>

                    {/* ================= META ================= */}
                    <section className="px-4 mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-sm text-[#FFFCF2] font-light font-sora">
                        <span>Status: <b className="text-white font-semibold">{contentStatus}</b></span>
                        <span>Language: <b className="text-white font-semibold">English</b></span>
                        <span>First publish: <b className="text-white font-semibold">{moment(createdAt).format("MMM DD, YYYY")}</b></span>
                        <span>Last updated: <b className="text-white font-semibold">{moment(createdAt).fromNow()}</b></span>
                    </section>

                    {/* ================= TABLE OF CONTENT ================= */}
                    <section className="px-4 sm:px-6 mt-4">
                        <h2 className="text-sm font-sora font-semibold mb-2.5">Table of content</h2>

                        <div className="grid grid-cols-2 sm:grid-cols-7 gap-3">
                            {visibleChapters?.sort((a, b) => a.chapterNumber - b.chapterNumber).map((ch) => {
                                const isUnlocked = ch.canRead
                                return (
                                    <button
                                        key={ch.chapterId}
                                        onClick={() => handleChapterClick(ch)}
                                        className={`px-[14px] py-2 rounded-[5px] text-[11px] flex justify-center items-center gap-1 bg-white/15`}
                                    >
                                        {isAuthor && ch.status === 'draft' && <RiDraftFill size={14} className="text-customGold" />}
                                        <span title={ch.title} className="truncate font-sora font-semibold -mb-[1px]">{ch.title}</span>
                                        {!isUnlocked && !isAuthor && <FaLock size={14} className="text-customGold" />}
                                    </button>
                                );
                            })}
                        </div>

                        {chapters?.length > DEFAULT_VISIBLE && (
                            <button
                                onClick={() => setShowAllChapters((prev) => !prev)}
                                className="mt-4 text-sm text-[#BE9524]"
                            >
                                {showAllChapters ? "See Less ↑" : "See More ↓"}
                            </button>
                        )}
                    </section>

                    {/* ================= AUTHOR CARD ================= */}
                    <section className="px-4 sm:px-8 mt-8 flex flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <img
                                src={profileImage || defaultAvatar}
                                alt="author profile"
                                className="rounded-full w-[67px] bg-white aspect-square sm:w-20 sm:h-20"
                            />
                            <div className="flex flex-col">
                                <p className="font-semibold font-sora">{authorName}</p>
                                {UserName && <p className="text-[15px] text-white font-light font-outfit">BD@{UserName}</p>}
                            </div>
                        </div>
                        <button
                            disabled={addingToLibrary}
                            onClick={handleAddToLibrary}
                            className="px-4 py-2 rounded-[4px] bg-white/15 text-xs font-outfit flex items-center gap-2 disabled:opacity-70"
                        >
                            {addedToLibrary ? <FaBookmark size={18} /> : <CiBookmark size={18} />} {addedToLibrary ? "Remove from Library" : 'Add to Library'}
                        </button>
                    </section>

               {/* ================= SOCIAL + RATE BUTTONS ================= */}

<section className="px-4 sm:px-8 mt-2 w-full">
    <div className="flex items-stretch gap-2 w-full sm:w-fit">

        {!isAuthor && (
            <div className="flex items-stretch">
                <FollowButton
                    author={authorWithFollowStatus}
                    onFollowChange={handleFollowChange}
                />
            </div>
        )}

        {/* Share Button */}
        <button
            type="button"
            className="
                h-10
                px-4
                flex
                items-center
                justify-center
                rounded-[5px]
                bg-customDark
                text-white
                hover:bg-[#4A4A45]
                transition-colors
                shrink-0
            "
            aria-label="Share"
        >
            <PiShareFatFill
                size={18}
                className="text-[#BE9524]"
            />
        </button>

        {/* Rate Button */}
        <button
            type="button"
            onClick={() => setShowRatingModal(true)}
            className="
                h-10
                px-4
                flex
                items-center
                justify-center
                gap-1.5
                rounded-[5px]
                bg-[#1C1B19]
                text-white
                text-sm
                hover:bg-[#4A4A45]
                transition-colors
                shrink-0
            "
        >
            <FaStar
                size={18}
                className="text-customGold"
            />

            <span className="text-[12px] font-medium font-outfit">
                Rate
            </span>
        </button>

    </div>
</section>

                    {/* ✅ FIXED: Rating Modal with refresh on close */}
                    <RatingModal
                        narrateId={narrateId}
                        isOpen={showRatingModal}
                        onClose={handleRatingModalClose}
                        title={title}
                    />

                    {/* ================= COMMENTS ================= */}
                    <Narray8Comment narrateId={narrateId} authorId={userId} />

                    {/* ================= MOBILE STICKY ================= */}
                    <div className="fixed bottom-0 left-0 w-full bg-[#2B2B27] p-4 flex justify-center sm:hidden">
                        <button
                            onClick={() => {
                                if (lastReadChapterId !== null) {
                                    const index = chapters?.findIndex(ch => ch.chapterId === lastReadChapterId);
                                    if (index !== -1) {
                                        handleChapterClick(chapters[index]);
                                        return;
                                    }
                                } else {
                                    handleChapterClick(chapters?.[0]);
                                }
                            }}
                            className="h-[50px] w-full max-w-md bg-black rounded-lg flex items-center justify-center gap-2"
                        >
                            <BookOpen size={18} />
                            {lastRead !== null ? "Continue Reading" : "Start Reading"}
                        </button>
                    </div>

                    {/* ================= COIN UNLOCK MODAL ================= */}
                    <ChapterPurchaseFlow
                        narray8Id={narrateId}
                        open={unlockModalOpen}
                        setOpen={setUnlockModalOpen}
                        chapters={chapters?.filter(ch => !ch?.canRead)?.sort((a, b) => a.chapterNumber - b.chapterNumber) || []}
                    />
                </>
            }

            {showStats &&
                <section className="flex flex-col gap-6 pb-12">
                    <div className="p-3 bg-customGray shadow-black/10 shadow-xl flex items-center justify-center text-2xl font-outfit font-semibold line-clamp-1">
                        {title}
                    </div>

                    {chapters?.length === 0 ? (
                        <div className="p-6 bg-customGray rounded-lg text-center text-sm font-sora">
                            No chapters available.
                        </div>
                    ) :
                        (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[720px] text-sm">
                                    <thead className="text-left text-[15px] font-semibold font-sora">
                                        <tr className="border-b border-[#626465] px-6 md:px-10">
                                            <th className="px-4 py-2">Chapter</th>
                                            <th className="px-4 py-2">Published Date</th>
                                            <th className="px-4 py-2">Chapter Price</th>
                                            <th className="px-4 py-2">Total Puchases</th>
                                            <th className="px-4 py-2">Earnings</th>
                                        </tr>
                                    </thead>

                                    <tbody className="text-base font-sora px-6 md:px-10">
                                        {chapters?.sort((a, b) => a.chapterNumber - b.chapterNumber).map((ch) => (
                                            <tr key={ch.chapterId} className="text-left">
                                                <td title={ch.title} className="px-4 py-3 line-clamp-1">{ch.title}</td>
                                                <td className="px-4 py-3">{ch.status === "published" ? moment(ch.publishedAt).format("MMM DD, YYYY") : "Draft"}</td>
                                                <td className="px-4 py-3 flex items-center gap-1">{ch.chapterNumber <= 5 ? "" : <BDcoin size={18} />} {ch.chapterNumber <= 5 ? "--" : (ch.price || 0)}</td>
                                                <td className="px-4 py-3">{ch.chapterNumber <= 5 ? "--" : ch.totalPurchases}</td>
                                                <td className="px-4 py-3 flex items-center gap-1">{ch.chapterNumber <= 5 ? "" : <BDcoin size={18} />} {ch.chapterNumber <= 5 ? "--" : (ch.totalEarnings || 0)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )
                    }
                </section>
            }
        </div>
    );
}